import React,{useEffect,useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useActiveEvents} from '../../hooks/useActiveEvents';
import {formatDate} from '../../utils/formatDate';
import {buildPath} from '../../utils/routes';
import {track} from '../../services/activityService';

const {FiChevronLeft,FiChevronRight,FiCalendar,FiMapPin,FiClock,FiUser,FiExternalLink,FiLink}=FiIcons;

const TYPES={
  examen: 'Examen',
  torneo: 'Torneo',
  convivio: 'Convivio',
  'clase-especial': 'Clase especial',
};

const MONTHS=[
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

const trackEvent=(event,origen)=> track('detalle_evento',{
  module: 'eventos',
  itemId: event.id,
  itemName: event.title,
  details: {tipo: event.type,fecha: event.eventDate,origen},
});

// Un evento ya pasó cuando su fecha y hora quedaron atrás.
const isPastEvent=(event)=> new Date(`${event.eventDate}T${event.time || '23:59'}`)<new Date();

const mapQuery=(place)=> encodeURIComponent(place);

// Si el mapa se capturó con coordenadas, el enlace "Abrir en Google Maps" apunta a ese punto exacto.
const mapPoint=(event)=> {
  const coords=event.mapUrl?.match(/[?&]q=(-?[\d.]+,-?[\d.]+)/)?.[1];
  return coords || mapQuery(event.place);
};

const getEventMonth=(event)=>{
  if (!event?.eventDate) {
    return new Date(new Date().getFullYear(),new Date().getMonth(),1);
  }

  const [year,month]=event.eventDate.split('-').map(Number);
  return new Date(year,month-1,1);
};

export default function EventsView({initialEvent,eventId}) {
  const [month,setMonth]=useState(()=> getEventMonth(initialEvent));
  const [selected,setSelected]=useState(initialEvent || null);
  const events=useActiveEvents();
  const [copied,setCopied]=useState(false);

  useEffect(()=>{
    if (!initialEvent) return;

    setMonth(getEventMonth(initialEvent));
    setSelected(initialEvent);
    trackEvent(initialEvent,'carrusel');

    setTimeout(()=>{
      document.getElementById('event-detail')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },100);
  },[initialEvent]);

  // Sin evento elegido, se muestra por defecto el próximo evento más cercano.
  useEffect(()=>{
    if (selected || initialEvent || eventId) return;

    const now=new Date();
    const next=events
      .filter(event=> new Date(`${event.eventDate}T${event.time || '00:00'}`)>=now)
      .sort((a,b)=> `${a.eventDate}${a.time}`.localeCompare(`${b.eventDate}${b.time}`))[0];

    if (next) {
      setSelected(next);
      setMonth(getEventMonth(next));
    }
  },[events,selected,initialEvent,eventId]);

  // Enlace directo (/eventos/:id): se selecciona ese evento en cuanto cargan los eventos.
  useEffect(()=>{
    if (!eventId || initialEvent) return;

    const target=events.find(event=> event.id===eventId);
    if (!target) return;

    setSelected(target);
    setMonth(getEventMonth(target));
    setTimeout(()=>{
      document.getElementById('event-detail')?.scrollIntoView({behavior: 'smooth',block: 'start'});
    },200);
  },[events,eventId,initialEvent]);

  const copyEventLink=async ()=>{
    const path=buildPath({view: 'eventos',eventId: selected.id});
    try {
      await navigator.clipboard.writeText(window.location.origin+path);
      setCopied(true);
      setTimeout(()=> setCopied(false),2000);
    } catch {
      window.prompt('Copia este enlace:',window.location.origin+path);
    }
  };

  const days=useMemo(()=>{
    const start=new Date(month.getFullYear(),month.getMonth(),1);
    const total=new Date(month.getFullYear(),month.getMonth()+1,0).getDate();
    const offset=(start.getDay()+6)%7;

    return [
      ...Array(offset).fill(null),
      ...Array.from({length: total},(_,index)=> index+1),
    ];
  },[month]);

  const eventsForDay=(day)=> day
    ? events.filter(event=>{
      const date=new Date(`${event.eventDate}T00:00:00`);

      return date.getFullYear()===month.getFullYear()
        && date.getMonth()===month.getMonth()
        && date.getDate()===day;
    })
    : [];

  const monthEvents=events
    .filter(event=>{
      const [year,eventMonth]=event.eventDate.split('-').map(Number);
      return year===month.getFullYear() && eventMonth===month.getMonth()+1;
    })
    .sort((a,b)=> `${a.eventDate}${a.time}`.localeCompare(`${b.eventDate}${b.time}`));

  const selectEvent=(event)=>{
    setSelected(event);
    window.history.replaceState(null,'',buildPath({view: 'eventos',eventId: event.id}));
    trackEvent(event,'calendario');

    setTimeout(()=>{
      document.getElementById('event-detail')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },50);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 pb-24">
      <div className="text-center mb-12">
        <span className="text-red-500 font-black tracking-[.4em] uppercase text-[10px]">
          Agenda del Dojang
        </span>

        <h1 className="text-6xl font-black uppercase tracking-tighter text-white mt-3">
          Eventos
        </h1>

        <p className="text-white/60 mt-4">
          Consulta las próximas actividades, evaluaciones y encuentros.
        </p>
      </div>

      <div className="glass-card rounded-[2.5rem] p-5 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={()=> setMonth(new Date(month.getFullYear(),month.getMonth()-1,1))}
            className="p-3 bg-white/5 hover:bg-red-600 rounded-xl text-white"
            aria-label="Mes anterior"
          >
            <SafeIcon icon={FiChevronLeft} />
          </button>

          <h2 className="text-2xl font-black uppercase tracking-widest text-white">
            {MONTHS[month.getMonth()]} {month.getFullYear()}
          </h2>

          <button
            onClick={()=> setMonth(new Date(month.getFullYear(),month.getMonth()+1,1))}
            className="p-3 bg-white/5 hover:bg-red-600 rounded-xl text-white"
            aria-label="Mes siguiente"
          >
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
          {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(day=> (
            <span
              key={day}
              className="text-center text-[9px] font-black uppercase tracking-widest text-white/40 py-2"
            >
              {day}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 md:gap-2">
          {days.map((day,index)=> (
            <div
              key={`${day}-${index}`}
              className={`min-h-14 md:min-h-32 rounded-xl md:rounded-2xl border p-1 md:p-2 flex flex-col ${
                day ? 'bg-white/5 border-white/5' : 'border-transparent'
              }`}
            >
              {day && (
                <>
                  <span className="text-xs font-black text-white/50">{day}</span>

                  <div className="mt-2 flex-1 flex flex-col gap-1">
                    {eventsForDay(day).map(event=> {
                      const isPast=isPastEvent(event);
                      const isSelected=selected?.id===event.id;

                      return (
                        <button
                          key={event.id}
                          onClick={()=> selectEvent(event)}
                          className={`md:flex-1 w-full text-left rounded-lg md:rounded-xl p-0 h-5 md:h-auto md:p-2 text-[10px] md:text-xs font-black leading-tight break-words md:border-l-4 transition-colors ${
                            isPast
                              ? 'bg-zinc-500/60 border-zinc-400 text-zinc-100 hover:bg-zinc-500/80'
                              : 'bg-white border-red-600 text-zinc-900 hover:bg-zinc-100'
                          } ${isSelected ? 'ring-2 ring-yellow-300' : ''}`}
                        >
                          <span className="hidden md:inline">{event.title}</span>
                          <span className="md:hidden flex h-full items-center justify-center">
                            <span className={`block h-2.5 w-2.5 rounded-full ${isPast ? 'bg-zinc-300' : 'bg-red-600'}`} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {monthEvents.length>0 && (
        <div className="md:hidden glass-card rounded-[2rem] p-5 mt-4 space-y-2">
          <h3 className="text-xs font-black uppercase tracking-[0.3em] text-white [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]">
            Eventos de {MONTHS[month.getMonth()]}
          </h3>
          {monthEvents.map(event=> (
            <button
              key={event.id}
              onClick={()=> selectEvent(event)}
              className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left border-l-4 ${
                isPastEvent(event)
                  ? 'bg-zinc-500/60 border-zinc-400 text-zinc-100'
                  : 'bg-white border-red-600 text-zinc-900'
              } ${selected?.id===event.id ? 'ring-2 ring-yellow-300' : ''}`}
            >
              <span className="text-lg font-black tabular-nums w-8">{Number(event.eventDate.split('-')[2])}</span>
              <span className="text-xs font-black leading-tight">{event.title}</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <motion.section
          id="event-detail"
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          className="glass-card rounded-[2.5rem] p-7 md:p-10 mt-8 scroll-mt-28"
        >
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-red-400 text-[10px] font-black uppercase tracking-widest">
              {TYPES[selected.type]}
            </span>

            <span className="text-green-400 text-[10px] font-black uppercase tracking-widest">
              Activo
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter text-white">
            {selected.title}
          </h2>

          <p className="text-white/80 leading-relaxed mt-5 max-w-3xl whitespace-pre-line">
            {selected.description}
          </p>

          {selected.image && (
            <div className="mt-6 flex justify-center">
              <img
                src={selected.image}
                alt={selected.title}
                className="w-full max-w-3xl max-h-[28rem] rounded-3xl object-cover border border-white/10 shadow-2xl"
              />
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-5 mt-8 text-xs font-bold uppercase tracking-widest text-white/70">
            <span className="flex gap-2 items-center">
              <SafeIcon icon={FiCalendar} className="text-red-500" />
              {formatDate(selected.eventDate)}
            </span>

            <span className="flex gap-2 items-center">
              <SafeIcon icon={FiClock} className="text-red-500" />
              {selected.time}
            </span>

            <span className="flex gap-2 items-center">
              <SafeIcon icon={FiMapPin} className="text-red-500" />
              {selected.place}
            </span>
          </div>

          {selected.place && (
            <div className="mt-8">
              <div className="aspect-video max-w-3xl overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
                <iframe
                  key={selected.id}
                  src={selected.mapUrl || `https://maps.google.com/maps?q=${mapQuery(selected.place)}&hl=es&z=16&iwloc=near&output=embed`}
                  title={`Mapa de ${selected.place}`}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapPoint(selected)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-300"
              >
                <SafeIcon icon={FiExternalLink} />
                Abrir en Google Maps
              </a>
            </div>
          )}

          <button
            type="button"
            onClick={copyEventLink}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/30 px-5 py-2.5 text-[11px] font-black uppercase tracking-widest text-white hover:bg-black/50"
          >
            <SafeIcon icon={FiLink} />
            {copied ? '¡Enlace copiado!' : 'Copiar enlace del evento'}
          </button>

          <p className="mt-6 text-[10px] uppercase tracking-widest text-white/35 flex items-center gap-2">
            <SafeIcon icon={FiUser} />
            Registrado por {selected.registeredBy}
          </p>
        </motion.section>
      )}
    </div>
  );
}